/**
 * Choosing the cut: one control, one value, both query params or neither.
 *
 * **One `<select>`, not two.** A question picker beside an option picker has a half state — a
 * question chosen and no option yet — and that half state is exactly the `400` the BFF answers,
 * because half a cut names no group of people. Grouping the options under their question with
 * `<optgroup>` makes the pair a single value, so the invalid state is not merely discouraged, it
 * cannot be expressed.
 *
 * **Only what the BFF would accept is offered** (R49). The list comes from `cuttableQuestions`:
 * choice questions and the rows of a grid of those types, of the version on screen. `RATING`,
 * `NPS`, `BOOLEAN` and free text are refused by the BFF, and a selector that offered them would
 * make a refusal the normal flow instead of the net it is meant to be.
 *
 * With nothing cuttable in this version the control does not render at all: an empty picker is a
 * promise the version cannot keep.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import type { JSX } from 'react';
import type { CuttableQuestion } from '../../../Utils/resultsCut';
import { cutValue, decodeCut, encodeCut } from '../../../Utils/resultsCut';
import type { SurveyResultsCut } from '../../../Types/Surveys.types';

const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const labelStyle = css`
  font-size: var(--font-size-2xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
`;

const selectStyle = css`
  height: 40px;
  max-width: 320px;
  padding: 0 var(--space-s);
  border-radius: var(--radius-control);
  border: 1px solid var(--color-border-strong);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--font-size-sm);
`;

/** While a cut is on, the control itself has to look chosen, not merely hold a value. */
const activeSelectStyle = css`
  border-color: var(--color-primary);
  box-shadow: 0 0 0 1px var(--color-primary);
`;

export interface ResultsCutControlProps {
  id: string;
  questions: CuttableQuestion[];
  cut: SurveyResultsCut | undefined;
  onChange: (cut: SurveyResultsCut | undefined) => void;
}

export const ResultsCutControl = ({
  id,
  questions,
  cut,
  onChange,
}: ResultsCutControlProps): JSX.Element | null => {
  if (questions.length === 0) return null;

  return (
    <div className={fieldStyle}>
      <span className={labelStyle}>Recorte por uma resposta</span>
      <select
        id={id}
        className={`${selectStyle} ${cut ? activeSelectStyle : ''}`}
        value={cutValue(cut)}
        aria-label='Recortar os números por pergunta por uma resposta'
        onChange={event => {
          onChange(decodeCut(event.target.value));
        }}
      >
        <option value=''>Sem recorte — todas as pessoas que enviaram</option>
        {questions.map(question => (
          <optgroup key={question.key} label={question.label}>
            {question.options.map(option => (
              <option
                key={`${question.key}-${option.value}`}
                value={encodeCut(question.key, option.value)}
              >
                {option.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
};
